import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { apiService, PromoCode } from '../services/api';
import PromoCodeCreateModal from './PromoCodeCreateModal';
import PromoCodeEditModal from './PromoCodeEditModal';
import PromoCodeViewModal from './PromoCodeViewModal';
import ConfirmModal from './ConfirmModal';

interface PromoCodeManagementProps {
  onBack?: () => void;
}

const PromoCodeManagement: React.FC<PromoCodeManagementProps> = ({ onBack }) => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<string>('all');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCode | null>(null);
    const [confirmModal, setConfirmModal] = useState<{
        visible: boolean;
        promoCode: PromoCode | null;
        message: string;
    }>({
        visible: false,
        promoCode: null,
        message: '',
    });

    const fetchPromoCodes = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiService.getPromoCodes({
                page: currentPage,
                limit: pageSize,
                search: searchTerm || undefined,
                isActive: isActiveFilter !== 'all' ? isActiveFilter === 'true' : undefined,
            });
            setPromoCodes(response.promoCodes || []);
            setTotal(response.pagination?.total || 0);
        } catch (error) {
            console.error('Failed to fetch promo codes:', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchTerm, isActiveFilter]);

    useEffect(() => {
        fetchPromoCodes();
    }, [fetchPromoCodes]);

  const handleCreate = () => {
    setIsCreateModalVisible(true);
  };

  const handleEdit = (promoCode: PromoCode) => {
    setSelectedPromoCode(promoCode);
    setIsEditModalVisible(true);
  };

  const handleView = (promoCode: PromoCode) => {
    setSelectedPromoCode(promoCode);
    setIsViewModalVisible(true);
  };

    const handleDelete = (promoCode: PromoCode) => {
      setConfirmModal({
        visible: true,
        promoCode,
        message: `Are you sure you want to delete promo code (${promoCode.code})?`,
      });
    };

    const confirmDelete = async () => {
      if (!confirmModal.promoCode) return;
      
      try {
        await apiService.deletePromoCode(confirmModal.promoCode.id);
        setConfirmModal({ visible: false, promoCode: null, message: '' });
        fetchPromoCodes();
      } catch (error) {
        console.error('Failed to delete promo code:', error);
        alert('Failed to delete promo code. Please try again.');
      }
    };

  const handleCreateSuccess = () => {
    setIsCreateModalVisible(false);
    fetchPromoCodes();
  };

  const handleEditSuccess = () => {
    setIsEditModalVisible(false);
    setSelectedPromoCode(null);
    fetchPromoCodes();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDiscount = (type: string, value: number) => {
    if (type === 'percentage') {
      return `${value}%`;
    }
    return formatCurrency(value);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900">Promo Code Management</h1>
        </div>
        <div className="text-sm text-gray-500">
          Total Promo Codes: {total}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by code, name, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-imola-primary focus:border-imola-primary sm:text-sm"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={isActiveFilter}
              onChange={(e) => setIsActiveFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value="all">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Page Size:</label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
          <button
            onClick={handleCreate}
            className="bg-imola-primary hover:bg-imola-primary/80 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add Promo Code
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Products
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : promoCodes.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    No promo codes found
                  </td>
                </tr>
              ) : (
                promoCodes.map((promoCode) => (
                  <tr key={promoCode.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded border">
                        {promoCode.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 font-mono bg-blue-50 px-2 py-1 rounded border border-blue-200">
                        {promoCode.code}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 text-left">{promoCode.name}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs text-left">{promoCode.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 text-left">
                        {formatDiscount(promoCode.discountType, Number(promoCode.discountValue))}
                      </div>
                      <div className="text-xs text-gray-500 text-left">{promoCode.discountType}</div>
                      {promoCode.maxDiscount && (
                        <div className="text-xs text-gray-400 text-left">Max: {formatCurrency(Number(promoCode.maxDiscount))}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 text-left">
                        {formatDate(promoCode.startDate)} - {formatDate(promoCode.endDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 text-left">
                        {promoCode.products?.length || 0} product(s)
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        promoCode.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {promoCode.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(promoCode)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(promoCode)}
                          className="text-green-600 hover:text-green-900 p-1"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(promoCode)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                disabled={currentPage >= Math.ceil(total / pageSize)}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, total)}
                  </span>{' '}
                  of <span className="font-medium">{total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                    disabled={currentPage >= Math.ceil(total / pageSize)}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <PromoCodeCreateModal
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onSuccess={handleCreateSuccess}
      />

      <PromoCodeEditModal
        visible={isEditModalVisible}
        promoCode={selectedPromoCode}
        onCancel={() => {
          setIsEditModalVisible(false);
          setSelectedPromoCode(null);
        }}
        onSuccess={handleEditSuccess}
      />

            <PromoCodeViewModal
                visible={isViewModalVisible}
                promoCode={selectedPromoCode}
                onCancel={() => {
                    setIsViewModalVisible(false);
                    setSelectedPromoCode(null);
                }}
            />

            <ConfirmModal
                visible={confirmModal.visible}
                title="Confirm Delete"
                message={confirmModal.message}
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonClass="bg-red-600 hover:bg-red-700"
                onConfirm={confirmDelete}
                onCancel={() => setConfirmModal({ visible: false, promoCode: null, message: '' })}
            />
        </div>
    );
};

export default PromoCodeManagement;

